import { clsx } from 'clsx'
import { type FC, type JSX, type ReactNode, useId } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { IconArrowRight } from '@redteclab/icons'

import { Button, Link } from 'base-ui'

import {
  FEATURE_TOGGLE_VOLUME_BASED_PRICES,
  useFeatureToggleContext,
} from '../../../../../feature-toggles'
import { useSearchParamDependentOpenableControls } from '../../../../../generic-hooks'
import { priceGetFormattedPrice } from '../../../../../price'
import { offerAccessorOtherOffersHaveVolumePricing } from '../../../../model/offerAccessor/offerAccessorOtherOffersHaveVolumePricing'
import { productAccessorGetSaeOffer } from '../../../../model/productAccessor/productAccessorGetSaeOffer'
import { productAccessorGetUniqueVariantSellersCount } from '../../../../model/productAccessor/productAccessorGetUniqueVariantSellersCount'
import { productAccessorProductHasSellerPromotionOnDifferentOffer } from '../../../../model/productAccessor/productAccessorProductHasSellerPromotionOnDifferentOffer'
import { useProductPageContext } from '../../../productPageContext'

import { ProductOfferBoxGenericLabel } from './ProductOfferBoxGenericLabel'
import { ProductOfferDrawer } from './ProductOfferDrawer'
import { ProductOfferVolumePricingLabel } from './ProductOfferVolumePricingLabel'
import { useGtmDataLayerPushProductOffersDrawerView } from './useGtmDataLayerPushProductOffersDrawerView'
import { useProductSellerSelectionBoxData } from './useProductSellerSelectionBoxData'

const getSellersCounter = (chunks: ReactNode): JSX.Element => <u>{chunks}</u>

export const ProductOfferBox: FC<{
  isMarketplaceHarmonisationEnabled: boolean
}> = ({ isMarketplaceHarmonisationEnabled }) => {
  const {
    currentOffer,
    currentVariant,
    currentVariant: { offers },
  } = useProductPageContext()
  const intl = useIntl()
  const { handleClose, handleOpen, isOpen } =
    useSearchParamDependentOpenableControls({
      paramName: 'offerSelectionDrawer',
    })

  useGtmDataLayerPushProductOffersDrawerView({ isOpen })

  const { bestPriceFlaggedOfferPrice, shouldRenderSelectionBox } =
    useProductSellerSelectionBoxData()

  const id = useId()

  const otherOffersHaveVolumePricing =
    offerAccessorOtherOffersHaveVolumePricing(offers, currentOffer)

  const featureToggles = useFeatureToggleContext()

  if (!shouldRenderSelectionBox) {
    return null
  }

  const sellersCount =
    productAccessorGetUniqueVariantSellersCount(currentVariant)

  const isSaeOffer = Boolean(productAccessorGetSaeOffer({ offers }))

  const formattedMessageValues = {
    count: isSaeOffer ? sellersCount - 1 : sellersCount,
    // eslint-disable-next-line id-length -- l10n parameter
    u: getSellersCounter,
  }

  const isVolumePricingFeatureEnabled = Boolean(
    featureToggles[FEATURE_TOGGLE_VOLUME_BASED_PRICES],
  )

  return (
    <div className="mb-5 flex items-center justify-between rounded border border-dark-primary-strong p-3">
      <div>
        <Link
          className="text-left font-bold text-dark-brand-medium underline hover:no-underline"
          id={id}
          onClick={handleOpen}
          slots={{ root: 'button' }}
          type="button"
        >
          {isSaeOffer ? (
            <FormattedMessage
              id="offerBox.link.label.both"
              values={formattedMessageValues}
            />
          ) : (
            <FormattedMessage
              id="offerBox.link.label.sellers.only"
              values={formattedMessageValues}
            />
          )}
        </Link>
        <div className="text-xl text-dark-primary-max">
          <FormattedMessage
            id="general.label.product.price.from"
            values={{
              price: priceGetFormattedPrice(
                bestPriceFlaggedOfferPrice?.price ?? 0,
                intl,
              ),
            }}
          />
        </div>
        <div className="flex items-center">
          {productAccessorProductHasSellerPromotionOnDifferentOffer(
            currentOffer,
            offers,
          ) ? (
            <ProductOfferBoxGenericLabel
              className={clsx(
                'h-fit border-dark-primary-min',
                isMarketplaceHarmonisationEnabled &&
                  'border-none bg-light-tertiary',
              )}
              data-qa-id="product-label-seller-promotion-offer-box"
            >
              <FormattedMessage id="seller.promotion.product.badge" />
            </ProductOfferBoxGenericLabel>
          ) : null}
          {otherOffersHaveVolumePricing && isVolumePricingFeatureEnabled ? (
            <ProductOfferVolumePricingLabel className="my-1.5 mr-1.5 border border-dark-primary-min bg-light-primary-low px-3 py-1" />
          ) : null}
        </div>
      </div>
      <Button
        aria-labelledby={id}
        className="px-3"
        data-qa-id="pdpOpenOfferSelectionDrawerBox"
        onClick={handleOpen}
      >
        <IconArrowRight className="text-3xl" />
      </Button>
      <ProductOfferDrawer handleClose={handleClose} isOpen={isOpen} />
    </div>
  )
}
